// src/pages/salary/EgyptianSalaryPage.jsx
import React, { useState, useEffect, useContext } from 'react';
import { DollarSign, Calculator, Shield, Award, TrendingUp, Calendar, Loader, Printer } from '../../constant/icons';
import ReusableDropdown from '../../components/ReusableDropdown';
import AlertModal       from '../../components/AlertModal';
import { ThemeContext }    from '../theme';
import { LanguageContext } from '../LanguageContext';

import { translations, getMonthName } from './config/translations_Salary';
import { getSalaryStyles, getHoverStyles, buildSalaryStats, buildStatsGridStyles } from './styles/salaryStyles';
import { usePayrollData } from './hooks/usePayrollData';
import StatsGrid          from '../../components/StatsGrid';
import InstancesTable     from './components/InstancesTable';
import Mo3tiCard          from './components/Mo3tiCard';
import DeductionsSection  from './components/DeductionsSection';
import AllowancesSection  from './components/AllowancesSection';
import SalaryComposition  from './components/SalaryComposition';
import PrintablePayslip   from './components/PrintablePayslip';

const EgyptianSalaryPage = ({ apiBaseUrl = process.env.REACT_APP_BASE_URL }) => {
  const { theme }    = useContext(ThemeContext);
  const { language } = useContext(LanguageContext);
  const isRTL        = language === 'ar';
  const t            = translations[language] || translations.en;

  // ── responsive ───────────────────────────────────────────────
  const [screenSize, setScreenSize] = useState({
    width: window.innerWidth, height: window.innerHeight,
    isMobile: window.innerWidth <= 768,
    isTablet: window.innerWidth > 768 && window.innerWidth <= 1024,
    isDesktop: window.innerWidth > 1024 && window.innerWidth <= 1440,
    isLargeDesktop: window.innerWidth > 1440,
  });

  useEffect(() => {
    const onResize = () => {
      const w = window.innerWidth;
      setScreenSize({ width: w, height: window.innerHeight, isMobile: w <= 768, isTablet: w > 768 && w <= 1024, isDesktop: w > 1024 && w <= 1440, isLargeDesktop: w > 1440 });
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // ── alert ────────────────────────────────────────────────────
  const [alertModal, setAlertModal] = useState({ open: false, title: '', message: '', variant: 'info' });
  const showAlert  = (title, message, variant = 'info') => setAlertModal({ open: true, title, message, variant });
  const closeAlert = () => setAlertModal((p) => ({ ...p, open: false }));

  // ── data hook ────────────────────────────────────────────────
  const {
    infinityCode, arabicName,
    loading, creating, error,
    payrollData, calculatedValues,
    selectedYear, setSelectedYear,
    selectedMonth, setSelectedMonth,
    availableMonths, availableYears,
  } = usePayrollData({ apiBaseUrl, t });

  // ── styles ───────────────────────────────────────────────────
  const styles = getSalaryStyles(theme, isRTL, screenSize);

  // Build stats from extracted functions in salaryStyles.js
  const icons = { DollarSign, Calculator, Shield, Award };
  const stats = buildSalaryStats(payrollData, calculatedValues, t, icons);
  const statsGridStyles = buildStatsGridStyles(styles, theme, isRTL);

  const handleMouseEnter = (e) => {
    if (!screenSize.isMobile) Object.assign(e.currentTarget.style, getHoverStyles(theme, false));
  };
  const handleMouseLeave = (e) => {
    if (!screenSize.isMobile) {
      e.currentTarget.style.transform   = 'translateY(0) scale(1)';
      e.currentTarget.style.boxShadow   = theme === 'dark' ? '0 8px 32px rgba(0,0,0,0.2)' : '0 8px 32px rgba(0,0,0,0.08)';
      e.currentTarget.style.background  = theme === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(255,255,255,0.9)';
      e.currentTarget.style.border      = theme === 'dark' ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(0,0,0,0.05)';
    }
  };

  // ── PDF download ─────────────────────────────────────────────
  const loadScript = (src) => new Promise((resolve, reject) => {
    if (document.querySelector(`script[src="${src}"]`)) return resolve();
    const s = document.createElement('script');
    s.src = src; s.onload = resolve; s.onerror = reject;
    document.head.appendChild(s);
  });

  const handleDownloadPDF = async () => {
    const el = document.querySelector('.printable-payslip');
    if (!el) { showAlert('Error', language === 'ar' ? 'لم يتم العثور على كشف الراتب' : 'Payslip not found', 'error'); return; }
    try {
      if (!window.html2canvas) await loadScript('https://cdnjs.cloudflare.com/ajax/libs/html2canvas/1.4.1/html2canvas.min.js');
      if (!window.jspdf)       await loadScript('https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js');

      const clone = el.cloneNode(true);
      clone.style.cssText = 'display:block;position:absolute;left:-9999px;top:0;width:800px;';
      const style = document.createElement('style');
      style.innerHTML = '.printable-payslip,.printable-payslip *{color:#111!important;-webkit-text-fill-color:#111!important;}';
      clone.insertBefore(style, clone.firstChild);
      document.body.appendChild(clone);

      // Wait two animation frames so the browser fully computes layout
      await new Promise(r => requestAnimationFrame(() => requestAnimationFrame(r)));

      const scale = Math.max(2, window.devicePixelRatio || 2);
      const pageWidth = 595.28;
      const pageHeight = 841.89;
      const margin = 20;
      const imgWidth = pageWidth - margin * 2;

      // ── Collect safe Y break points BEFORE rendering ──────────────────────
      // html2canvas ignores CSS print rules entirely, so we must find safe
      // cut positions ourselves by reading the rendered DOM element boundaries.
      // We cut only at the BOTTOM edge of a row/heading — never through one.
      const cloneRect = clone.getBoundingClientRect();
      const safeBreakSet = new Set([0]);

      // Hard section starts: always begin these sections at the top of a new PDF page.
      const forcedSectionStarts = [];
      clone.querySelectorAll('.allowance-instances-print-section, .deduction-instances-print-section').forEach((node) => {
        const r = node.getBoundingClientRect();
        const topPx = Math.round((r.top - cloneRect.top) * scale);
        if (topPx > 0) forcedSectionStarts.push(topPx);
      });
      forcedSectionStarts.sort((a, b) => a - b);

      clone.querySelectorAll('tr, .table-page, .pdf-section-start').forEach(node => {
        const r = node.getBoundingClientRect();
        // top of element = safe place to start a new page
        const topPx    = Math.round((r.top    - cloneRect.top) * scale);
        // bottom of element = safe place to end a page
        const bottomPx = Math.round((r.bottom - cloneRect.top) * scale);
        if (topPx    > 0) safeBreakSet.add(topPx);
        if (bottomPx > 0) safeBreakSet.add(bottomPx);
      });

      const safeBreaks = Array.from(safeBreakSet).sort((a, b) => a - b);
      // ─────────────────────────────────────────────────────────────────────

      const canvas = await window.html2canvas(clone, { scale, useCORS: true, backgroundColor: '#ffffff' });
      const jsPDFCtor = window.jspdf?.jsPDF || window.jsPDF || window.jspdf;
      if (!jsPDFCtor) throw new Error('jsPDF not available');

      const pdf = new jsPDFCtor('p', 'pt', 'a4');
      const pxPerPt   = canvas.width / imgWidth;
      const maxPagePx = Math.floor((pageHeight - margin * 2) * pxPerPt);

      let srcY = 0, page = 0;
      while (srcY < canvas.height) {
        const idealCut = srcY + maxPagePx;

        let cutY;
        if (idealCut >= canvas.height) {
          // Last (or only) page — take everything remaining
          cutY = canvas.height;
        } else {
          // If a forced section start exists inside this page window, cut exactly there
          // so that section begins at the top of the next page.
          let forcedCut = -1;
          for (let i = 0; i < forcedSectionStarts.length; i++) {
            const point = forcedSectionStarts[i];
            if (point > srcY && point <= idealCut) {
              forcedCut = point;
              break;
            }
          }

          if (forcedCut > 0) {
            cutY = forcedCut;
          } else {
          // Find the last safe break point that fits within this page
            let lastCandidate = -1;
            for (let i = 0; i < safeBreaks.length; i++) {
              const point = safeBreaks[i];
              if (point > srcY && point <= idealCut) lastCandidate = point;
              if (point > idealCut) break;
            }
            if (lastCandidate > 0) {
              cutY = lastCandidate; // last break that fits
            } else {
              // No safe break found (e.g. a single row taller than a page) — cut at page boundary
              cutY = idealCut;
            }
          }
        }

        const h = cutY - srcY;
        if (h <= 0) { srcY = cutY + 1; continue; } // safety guard

        const pc = document.createElement('canvas');
        pc.width = canvas.width; pc.height = h;
        const ctx = pc.getContext('2d');
        ctx.fillStyle = '#ffffff'; ctx.fillRect(0, 0, pc.width, h);
        ctx.drawImage(canvas, 0, srcY, canvas.width, h, 0, 0, canvas.width, h);

        if (page > 0) pdf.addPage();
        pdf.addImage(pc.toDataURL('image/png'), 'PNG', margin, margin, imgWidth, h / pxPerPt);
        srcY = cutY;
        page++;
      }

      const name = payrollData?.employeeName || arabicName || infinityCode || 'employee';
      pdf.save(`Payslip-${String(name).replace(/\s+/g, '_')}-${getMonthName(selectedMonth, t)}-${selectedYear}.pdf`);
      clone.remove();
    } catch (err) {
      console.error('PDF failed', err);
      showAlert('Error', language === 'ar' ? 'فشل في إنشاء PDF' : 'Failed to generate PDF', 'error');
    }
  };
 
  // ── loading / error / empty states ───────────────────────────
  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.backgroundElements}><div style={styles.circle1} /><div style={styles.circle2} /></div>
        <div style={{ ...styles.mainContent, ...styles.loadingContainer }}>
          <Loader size={48} color="#2e7cba" style={{ animation: 'spin 1s linear infinite' }} />
          <p style={styles.loadingText}>{creating ? t.creatingPayroll : t.loadingSalary}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={styles.container}>
        <div style={styles.backgroundElements}><div style={styles.circle1} /><div style={styles.circle2} /></div>
        <div style={styles.mainContent}>
          <div style={styles.errorBox}>
            <p style={styles.errorText}>{t.errorTitle}: {error}</p>
            <p style={{ ...styles.loadingText, marginTop: '8px' }}>{t.missingDataError}</p>
          </div>
        </div>
      </div>
    );
  }

  if (!calculatedValues) {
    return (
      <div style={styles.container}>
        <div style={styles.backgroundElements}><div style={styles.circle1} /><div style={styles.circle2} /></div>
        <div style={styles.mainContent}>
          <div style={styles.emptyBox}><p style={styles.loadingText}>{t.noSalaryData}</p></div>
        </div>
      </div>
    );
  }

  // ── main render ───────────────────────────────────────────────
  return (
    <div style={styles.container}>
      <div style={styles.backgroundElements}><div style={styles.circle1} /><div style={styles.circle2} /></div>

      <div style={styles.mainContent}>

        {/* Header */}
        <div style={styles.card1} >
          <h1 style={styles.headerTitle}>{t.pageTitle}</h1>
          <p style={styles.headerSubtitle}>{t.pageSubtitle}</p>
        </div>

        {/* Filter Bar */}
        <div style={styles.card1} className="no-print">
          <div style={styles.filterRow}>
            <Calendar size={20} color="#2e7cba" />

            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={styles.filterLabel}>{t.selectYear}:</span>
              <div style={{ minWidth: '120px' }}>
                <ReusableDropdown
                  items={availableYears.map((y) => ({ id: y, label: y.toString() }))}
                  value={selectedYear.toString()}
                  onChange={(val) => { const n = Number(val); if (!isNaN(n)) setSelectedYear(n); }}
                  onSelect={(item) => setSelectedYear(item.id)}
                  placeholder={t.selectYear}
                  theme={theme} language={language}
                  maxItems={10} showAllOnArrowClick showClear={false}
                  getItemKey={(i) => i.id} getItemLabel={(i) => i.label}
                  inputStyle={{ padding: '8px 40px 8px 16px', minWidth: '120px' }}
                />
              </div>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={styles.filterLabel}>{t.selectMonth}:</span>
              <div style={{ minWidth: '150px' }}>
                <ReusableDropdown
                  items={availableMonths.map((m) => ({ id: m, label: getMonthName(m, t) }))}
                  value={getMonthName(selectedMonth, t)}
                  onChange={(val) => { const m = availableMonths.find((x) => getMonthName(x, t) === val); if (m !== undefined) setSelectedMonth(m); }}
                  onSelect={(item) => setSelectedMonth(item.id)}
                  placeholder={t.selectMonth}
                  theme={theme} language={language}
                  maxItems={12} showAllOnArrowClick showClear={false}
                  getItemKey={(i) => i.id} getItemLabel={(i) => i.label}
                  inputStyle={{ padding: '8px 40px 8px 16px', minWidth: '150px' }}
                />
              </div>
            </div>

            <button
              onClick={handleDownloadPDF}
              style={styles.printButton}
              //onMouseLeave={(e) => { e.currentTarget.style.background = '#2e7cba'; e.currentTarget.style.transform = 'translateY(0)'; }}
              //onMouseEnter={(e) => { e.currentTarget.style.background = '#256ba1'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
            >
              <Printer size={16} />
              {t.printPayslip}
            </button>
          </div>
        </div>

        {/* Stats */}
        <StatsGrid styles={statsGridStyles} stats={stats} screenSize={screenSize} />

        {/* Sections */}
        <DeductionsSection payrollData={payrollData} calculatedValues={calculatedValues} styles={styles} t={t} theme={theme} isRTL={isRTL} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave} />
        <AllowancesSection payrollData={payrollData} styles={styles} t={t} theme={theme} isRTL={isRTL} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave} />
        <SalaryComposition payrollData={payrollData} calculatedValues={calculatedValues} styles={styles} t={t} theme={theme} isRTL={isRTL} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave} />

        {/* Allowance Instances Table */}
        <Mo3tiCard
          title={t.allowanceInstances}
          icon={TrendingUp}
          theme={theme}
          isRTL={isRTL}
        >
          <InstancesTable
            rows={Array.isArray(payrollData.allowances) ? payrollData.allowances : []}
            theme={theme}
            isRTL={isRTL}
            t={t}
            emptyText={t.noAllowances}
            amountColor="#16a34a"
            amountSign="+"
          />
        </Mo3tiCard>

        {/* Deduction Instances Table */}
        <Mo3tiCard
          title={t.deductionInstances}
          icon={Calculator}
          theme={theme}
          isRTL={isRTL}
        >
          <InstancesTable
            rows={Array.isArray(payrollData.deductions) ? payrollData.deductions : []}
            theme={theme}
            isRTL={isRTL}
            t={t}
            emptyText={t.noDeductionsRow}
            amountColor="#dc2626"
            amountSign="-"
          />
        </Mo3tiCard>
      </div>

      {/* Hidden printable payslip */}
      {payrollData && (
        <PrintablePayslip payrollData={payrollData} employee={{ infinityCode, arabicName }} theme={theme} t={t} isRTL={isRTL} selectedMonth={selectedMonth} selectedYear={selectedYear} calculatedValues={calculatedValues} />
      )}

      {/* Global CSS */}
      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        .hover-card { cursor: pointer; }
        .hover-card:hover {
          transform: ${screenSize.isMobile ? 'scale(1.02)' : 'translateY(-2px)'};
          box-shadow: ${theme === 'dark' ? '0 16px 48px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.2)' : '0 16px 48px rgba(0,0,0,0.15)'} !important;
          background: ${theme === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(255,255,255,0.95)'} !important;
          border: ${theme === 'dark' ? '1px solid rgba(255,255,255,0.25)' : '1px solid rgba(0,0,0,0.08)'} !important;
        }
        *::-webkit-scrollbar { width: ${screenSize.isMobile ? '4px' : '6px'}; }
        *::-webkit-scrollbar-track { background: ${theme === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)'}; border-radius: 3px; }
        *::-webkit-scrollbar-thumb { background: ${theme === 'dark' ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.2)'}; border-radius: 3px; }
        * { -webkit-tap-highlight-color: transparent; box-sizing: border-box; }
        @media (prefers-reduced-motion: reduce) { * { animation-duration: 0.01ms !important; transition-duration: 0.01ms !important; } }
      `}</style>

      <AlertModal open={alertModal.open} title={alertModal.title} message={alertModal.message} variant={alertModal.variant} onClose={closeAlert} theme={theme === 'dark' ? 'dark' : 'light'} language={language} />
    </div>
  );
};

export default EgyptianSalaryPage;